import {
  Alert,
  Box,
  Card,
  Flex,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { IconInfoCircle, IconUser } from '@tabler/icons-react';
import { MRT_TableOptions } from 'mantine-react-table';
import { useSelectedClaimStore } from 'store/SelectedClaimStore';
import BillingDataGrid from './BillingDataGrid';
import { formatPatientName } from '@billing/validation';

interface BillingDataGridWrapperProps {
  title: string;
  className?: string;
  subtitle?: string;
  subtitleColor?: string;
  titleAside?: React.ReactNode;
  dataTestID: string;
  tableOptions: MRT_TableOptions<any>;
  onTableReady?: (table: any) => void;
  showAlertBanner?: boolean;
  bannerAside?: React.ReactNode;
  preTableContent?: React.ReactNode;
  reservePatientBannerSpace?: boolean;
}

export const BillingDataGridWrapper: React.FC<BillingDataGridWrapperProps> = ({
  title,
  className,
  subtitle,
  subtitleColor = 'dimmed',
  titleAside,
  dataTestID,
  tableOptions,
  onTableReady,
  showAlertBanner = true,
  bannerAside,
  preTableContent,
  reservePatientBannerSpace = false,
}) => {
  const { selectedClaim, showPreview } = useSelectedClaimStore();
  const isPatientBannerVisible = Boolean(showPreview && selectedClaim);
  const shouldReservePatientBanner =
    reservePatientBannerSpace && showAlertBanner;
  const hasVisibleBannerContent = Boolean(
    bannerAside || isPatientBannerVisible || shouldReservePatientBanner
  );

  return (
    <Card
      className={className}
      p="md"
      data-testid={dataTestID}
      bg={'transparent'}
      style={{ backdropFilter: 'blur(10px)' }}
    >
      <Group justify="space-between" align="flex-end" gap="lg" wrap="wrap">
        <Stack gap={2}>
          <Title order={3} data-testid={`${dataTestID}-title`}>
            {title}
          </Title>
          {subtitle ? (
            <Text c={subtitleColor} fw={600} size="sm">
              {subtitle}
            </Text>
          ) : null}
        </Stack>
        {titleAside}
      </Group>
      {showAlertBanner && hasVisibleBannerContent ? (
        <Box data-testid={`${dataTestID}-patient-banner-slot`}>
          <Stack mt="sm" gap="sm">
            {bannerAside ? (
              <Box
                w="100%"
                maw="100%"
                data-testid={`${dataTestID}-banner-aside`}
              >
                {bannerAside}
              </Box>
            ) : null}
            {isPatientBannerVisible || shouldReservePatientBanner ? (
              <Box
                w="100%"
                maw="100%"
                mih={58}
                style={{
                  visibility: isPatientBannerVisible ? 'visible' : 'hidden',
                  pointerEvents: isPatientBannerVisible ? 'auto' : 'none',
                }}
                aria-hidden={!isPatientBannerVisible}
              >
                <TablePatientBanner />
              </Box>
            ) : null}
          </Stack>
        </Box>
      ) : null}
      {preTableContent ? (
        <Box pt="sm" data-testid={`${dataTestID}-pre-table-content`}>
          {preTableContent}
        </Box>
      ) : null}
      <Box pt="sm" data-testid={`${dataTestID}-table`}>
        <BillingDataGrid data={tableOptions} onTableReady={onTableReady} />
      </Box>
    </Card>
  );
};

export const TablePatientBanner = () => {
  const { selectedClaim, showPreview } = useSelectedClaimStore();
  const { translate } = useTranslation();
  const patient = selectedClaim?.patient;

  if (!showPreview || !selectedClaim) return null;

  const healthcard = patient?.healthcards?.find(
    (h) => selectedClaim.healthcardID === h.id
  );

  return showPreview && selectedClaim ? (
    <Flex justify="flex-start" align="center" h={58}>
      <Alert
        color="orange"
        p="xs"
        icon={<IconInfoCircle size={16} />}
        variant="outline"
        w="100%"
        h="100%"
        styles={{
          body: {
            minWidth: 0,
          },
          message: {
            minWidth: 0,
          },
        }}
      >
        <Group
          gap="lg"
          wrap="nowrap"
          style={{ minWidth: 0, overflow: 'hidden' }}
        >
          <Group gap="sm" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
            <IconUser size={16} />
            <Text fw={700} size="sm" truncate>
              {formatPatientName(patient?.firstName, patient?.lastName)}
            </Text>
          </Group>

          <Text size="sm" truncate>
            <strong>
              {translate(
                'common:components.patientBanner.healthcardLabel',
                'Healthcard:'
              )}
            </strong>{' '}
            {healthcard?.number ||
              translate(
                'common:components.patientBanner.noHealthcard',
                'No Healthcard'
              )}
          </Text>
          <Text size="sm" truncate>
            <strong>
              {translate(
                'common:components.patientBanner.registrationLabel',
                'Reg #:'
              )}
            </strong>{' '}
            {healthcard?.registration ||
              translate(
                'common:components.patientBanner.noRegistration',
                'No Reg'
              )}
          </Text>
          <Text size="sm" truncate>
            <strong>
              {translate('common:components.patientBanner.dobLabel', 'DOB:')}
            </strong>{' '}
            {patient?.dateOfBirth ||
              translate('common:components.patientBanner.noDob', 'No DOB')}
          </Text>
          <Text size="sm" truncate>
            <strong>
              {translate('common:components.patientBanner.sexLabel', 'Sex:')}
            </strong>{' '}
            {patient?.sex ||
              translate('common:components.patientBanner.noSex', 'No Sex')}
          </Text>
        </Group>
      </Alert>
    </Flex>
  ) : null;
};
